import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useLang } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useGravityToast } from '@/components/effects/GravityToast';
import api from '@/utils/api';
import ScrollReveal from '@/components/effects/ScrollReveal';
import { ShieldCheck, CreditCard, Loader2, CheckCircle, XCircle, ArrowLeft, Lock, MapPin, Package, Store, Truck, Heart, Gift } from 'lucide-react';
import PaymentMethods from '@/components/PaymentMethods';
import OrbitReferralCard from '@/components/OrbitReferralCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Checkout() {
  const { t, localizedField, lang } = useLang();
  const { user, loading: authLoading } = useAuth();
  // Guest checkout — no account needed; one is provisioned after payment
  const [guestEmail, setGuestEmail] = useState('');
  const [guestName, setGuestName] = useState('');
  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || '').trim());
  const deliveryLabel = (key) => t(`checkout.delivery.${key}`) || t('checkout.delivery.standard');
  const toast = useGravityToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const itemType = searchParams.get('type') || 'product';
  const itemId = searchParams.get('id');
  const currency = searchParams.get('currency') || 'USD';

  const ppToken = searchParams.get('token');
  const ppCancelled = searchParams.get('cancelled');

  const [item, setItem] = useState(null);
  const [paypalConfig, setPaypalConfig] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // Shipping
  const [isPhysical, setIsPhysical] = useState(false);
  const [shippingName, setShippingName] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [zip, setZip] = useState('');
  const [countryCode, setCountryCode] = useState('IL');
  const [phone, setPhone] = useState('');

  // Delivery
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [calcError, setCalcError] = useState(false);
  const [usePickup, setUsePickup] = useState(false);
  const [step, setStep] = useState('address'); // address | payment
  const [saveAsDefault, setSaveAsDefault] = useState(true);

  // Tip + Gift
  const [tipAmount, setTipAmount] = useState(0);
  const [tipCustom, setTipCustom] = useState('');
  const [isGift, setIsGift] = useState(false);
  const [giftRecipientEmail, setGiftRecipientEmail] = useState('');
  const [giftRecipientName, setGiftRecipientName] = useState('');
  const [giftMessage, setGiftMessage] = useState('');

  // Orbit Starter Pack bundle
  const [bundle, setBundle] = useState(null);
  const [bundleSelected, setBundleSelected] = useState(false);

  // Friend referral (20% off bundle)
  const [referralCode, setReferralCode] = useState('');
  const [referralInfo, setReferralInfo] = useState(null); // { valid, discount_pct, referrer_name }

  // Abandoned-checkout recovery code (10% off the whole order)
  const [recoveryCode, setRecoveryCode] = useState('');
  const [recoveryInfo, setRecoveryInfo] = useState(null); // { valid, discount_pct }

  // Pick up a recovery code from the URL (?recovery=COMEBACK-XXX)
  useEffect(() => {
    const code = (searchParams.get('recovery') || '').trim().toUpperCase();
    if (!code) return;
    setRecoveryCode(code);
    api.get(`/recovery/validate?code=${encodeURIComponent(code)}`)
      .then(({ data }) => setRecoveryInfo(data))
      .catch(() => setRecoveryInfo({ valid: false }));
  }, [searchParams]);

  // Pick up a friend-referral code from the URL (?ref=ORBIT-XXXXXX) or
  // from localStorage (landing-page visitors land on /book?ref=... first)
  useEffect(() => {
    const fromQuery = searchParams.get('ref');
    const fromStorage = localStorage.getItem('tw_ref_code');
    const code = (fromQuery || fromStorage || '').trim().toUpperCase();
    if (!code) return;
    setReferralCode(code);
    if (fromQuery) localStorage.setItem('tw_ref_code', code);
    api.post('/referrals/validate', { code })
      .then(({ data }) => {
        if (data?.valid) {
          setReferralInfo(data);
          setBundleSelected(true); // auto-select bundle so discount actually applies
        } else {
          setReferralInfo({ valid: false });
          localStorage.removeItem('tw_ref_code');
        }
      })
      .catch(() => setReferralInfo({ valid: false }));
  }, [searchParams]);

  // Determine if product is physical
  useEffect(() => {
    if (item) {
      const pType = item.type || item.metadata?.format || '';
      setIsPhysical(pType === 'physical' || pType === 'Hardcover' || pType === 'Paperback');
    }
  }, [item]);

  // Prefill name and default address
  useEffect(() => {
    if (user) {
      setShippingName(user.display_name || user.username || '');
      // Fetch default address
      api.get('/profiles/me/default-address').then(({ data }) => {
        if (data && data.address1) {
          setShippingName(data.name || user.display_name || user.username || '');
          setAddress1(data.address1 || '');
          setAddress2(data.address2 || '');
          setCity(data.city || '');
          setStateCode(data.state || '');
          setZip(data.zip || '');
          setCountryCode(data.country_code || 'IL');
          setPhone(data.phone || '');
        }
      }).catch(() => {});
    }
  }, [user]);

  // Fetch item
  useEffect(() => {
    if (ppToken) { setLoading(false); return; }
    if (!itemId) { setLoading(false); return; }
    const fetchData = async () => {
      try {
        const [configRes, bundleRes] = await Promise.all([
          api.get('/paypal/config'),
          api.get('/settings/orbit_starter_pack').catch(() => ({ data: null })),
        ]);
        setPaypalConfig(configRes.data);
        if (bundleRes?.data?.value?.enabled) setBundle(bundleRes.data.value);
        let endpoint = '';
        if (itemType === 'product') endpoint = `/products/${itemId}`;
        else if (itemType === 'course') endpoint = `/courses/${itemId}`;
        else if (itemType === 'event') endpoint = `/events/${itemId}`;
        if (endpoint) {
          const itemRes = await api.get(endpoint);
          setItem(itemRes.data);
        }
      } catch {
        setError('Failed to load checkout information');
      }
      setLoading(false);
    };
    fetchData();
  }, [itemId, itemType, ppToken]);

  // Handle PayPal return (redirect flow). Guest orders carry a short-lived
  // token in sessionStorage that proves this browser created the order.
  useEffect(() => {
    if (!ppToken || authLoading) return;
    let guestToken = '';
    try { guestToken = sessionStorage.getItem(`tw_guest_${ppToken}`) || ''; } catch { /* noop */ }
    if (!user && !guestToken) return;
    if (ppCancelled) {
      setPaymentResult({ success: false, message: 'Payment was cancelled.' });
      return;
    }
    const captureOrder = async () => {
      setProcessing(true);
      try {
        const res = await api.post(`/paypal/capture-order/${ppToken}`, {}, guestToken ? { headers: { 'X-Guest-Token': guestToken } } : undefined);
        if (res.data.status === 'COMPLETED') {
          setPaymentResult({
            success: true, orderId: ppToken,
            itemName: res.data.item_name, amount: res.data.amount,
            currency: res.data.currency, deliveryType: res.data.delivery_type,
            bundleSelected: !!res.data.bundle_selected,
            isGuest: !!res.data.is_guest, accountCreated: !!res.data.account_created,
          });
        } else {
          setPaymentResult({ success: false, message: 'Payment was not completed.' });
        }
      } catch {
        setPaymentResult({ success: false, message: 'Failed to process payment.' });
      }
      setProcessing(false);
    };
    captureOrder();
  }, [ppToken, ppCancelled, user, authLoading]);

  // Calculate delivery
  const calculateDelivery = useCallback(async () => {
    if (!city.trim() || !countryCode.trim()) return;
    setCalcLoading(true);
    setCalcError(false);
    try {
      const res = await api.post('/delivery/calculate', {
        city, region: stateCode, country_code: countryCode, currency,
        name: shippingName, address1, zip, state: stateCode, phone,
      });
      setDeliveryInfo(res.data);
      setUsePickup(false);
    } catch {
      setCalcError(true);
    }
    setCalcLoading(false);
  }, [city, countryCode, stateCode, currency, shippingName, address1, zip, phone]);

  // Auto-calc when city/country changes. The callback lives in a ref so
  // typing in unrelated fields (name/zip/phone) doesn't re-fire the request.
  const calcRef = useRef(calculateDelivery);
  useEffect(() => { calcRef.current = calculateDelivery; }, [calculateDelivery]);
  useEffect(() => {
    if (isPhysical && city.trim().length >= 2 && countryCode.trim()) {
      const timer = setTimeout(() => calcRef.current(), 600);
      return () => clearTimeout(timer);
    }
  }, [city, countryCode, isPhysical]);

  const validateShipping = () => {
    if (!shippingName.trim()) return t('checkout.nameRequired');
    if (!address1.trim()) return t('checkout.addressRequired');
    if (!city.trim()) return t('checkout.cityRequired');
    if (!countryCode.trim()) return t('checkout.countryRequired');
    if (!zip.trim()) return t('checkout.zipRequired');
    if (!phone.trim()) return t('checkout.phoneRequired');
    return null;
  };

  const handleProceedToPayment = () => {
    if (!user && !isValidEmail(guestEmail)) { toast.error(t('checkout.guestEmailRequired')); return; }
    if (isPhysical) {
      const err = validateShipping();
      if (err) { toast.error(err); return; }
      if (!deliveryInfo) {
        toast.error(calcError ? t('checkout.calcFailed') : t('checkout.waitForDelivery'));
        if (!calcLoading) calculateDelivery();
        return;
      }
    }
    setStep('payment');
  };

  const buildOrderPayload = async () => {
    if (!user && !isValidEmail(guestEmail)) {
      toast.error(t('checkout.guestEmailRequired'));
      throw new Error(t('checkout.guestEmailRequired'));
    }
    if (isGift && !isValidEmail(giftRecipientEmail)) {
      toast.error(t('checkout.giftEmailInvalid'));
      throw new Error(t('checkout.giftEmailInvalid'));
    }
    const deliveryType = !isPhysical ? 'digital' : usePickup ? 'pickup' : (deliveryInfo?.delivery_type || 'israel_standard');
    const deliveryCost = usePickup ? 0 : (deliveryInfo?.delivery_cost || 0);

    // Save address as user default if checked (non-blocking)
    if (user && isPhysical && saveAsDefault && !usePickup) {
      try {
        await api.put('/profiles/me/default-address', {
          name: shippingName, address1, address2, city,
          state: stateCode, zip, country_code: countryCode, phone,
        });
      } catch { /* non-blocking */ }
    }

    // Track checkout session for abandoned-cart recovery (non-blocking)
    try {
      await api.post('/checkout/session/start', {
        email: user?.email || guestEmail.trim().toLowerCase(),
        name: shippingName || user?.display_name || guestName || '',
        item_type: itemType, item_id: itemId, currency,
        email_lang: user?.email_lang || lang || 'en',
      });
    } catch { /* non-blocking */ }

    const returnBase = window.location.origin;
    const payload = {
      item_type: itemType, item_id: itemId, currency,
      guest_email: user ? '' : guestEmail.trim().toLowerCase(),
      guest_name: user ? '' : (guestName || shippingName).trim(),
      email_lang: lang || 'en',
      delivery_type: deliveryType,
      delivery_cost: deliveryCost,
      tip_amount: Number(tipAmount) || 0,
      bundle_selected: !!bundleSelected,
      referral_code: referralInfo?.valid ? referralCode : '',
      recovery_code: recoveryInfo?.valid ? recoveryCode : '',
      gift: isGift ? {
        enabled: true,
        recipient_email: giftRecipientEmail.trim(),
        recipient_name: giftRecipientName.trim(),
        message: giftMessage.trim(),
      } : { enabled: false },
      return_url: `${returnBase}/checkout`,
      cancel_url: `${returnBase}/checkout?cancelled=true&type=${itemType}&id=${itemId}&currency=${currency}`,
    };
    if (isPhysical) {
      payload.shipping_address = {
        name: shippingName, address1, address2, city,
        state: stateCode, zip, country_code: countryCode, phone,
      };
    }
    if (deliveryInfo?.lulu_shipping_options?.length) {
      payload.shipping_level = deliveryInfo.lulu_shipping_options[0]?.level || 'MAIL';
    }
    return payload;
  };

  const handlePaymentSuccess = (data) => {
    setPaymentResult({
      success: true,
      orderId: data.orderId,
      itemName: data.item_name || localizedField(item?.title) || '',
      amount: data.amount || totalPrice,
      currency: data.currency || currency,
      deliveryType: data.delivery_type,
      bundleSelected: !!(data.bundle_selected ?? bundleSelected),
      isGuest: !!data.is_guest, accountCreated: !!data.account_created,
    });
    setError('');
    // Clear any consumed referral code — it's now linked to this order
    if (referralInfo?.valid) localStorage.removeItem('tw_ref_code');
  };

  const handlePaymentError = (msg) => {
    setError(msg || 'Payment could not be completed.');
    setProcessing(false);
  };

  const handleContactForPickup = () => {
    window.open('/contact', '_blank');
  };

  const price = item ? (currency === 'USD' ? item.price_usd : (item.price_ils || item.price_usd)) : 0;
  const currencySymbol = currency === 'ILS' ? '\u20AA' : '$';
  const deliveryCost = usePickup ? 0 : (deliveryInfo?.delivery_cost || 0);
  const tipValue = Number(tipAmount) || 0;
  const bundleBase = (bundleSelected && bundle) ? (currency === 'USD' ? Number(bundle.price_usd || 0) : Number(bundle.price_ils || 0)) : 0;
  const referralDiscount = (referralInfo?.valid && bundleBase > 0) ? Math.round(bundleBase * 0.20 * 100) / 100 : 0;
  const bundleAmount = Math.max(0, bundleBase - referralDiscount);
  const subtotalBeforeRecovery = price + (isPhysical ? deliveryCost : 0) + tipValue + bundleAmount;
  const recoveryDiscount = (recoveryInfo?.valid) ? Math.round(subtotalBeforeRecovery * 0.10 * 100) / 100 : 0;
  const totalPrice = Math.max(0, subtotalBeforeRecovery - recoveryDiscount);

  // Wait for the session check — otherwise every buyer (including those
  // returning from PayPal) briefly hits the login wall
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 pt-24 pb-16" data-testid="checkout-auth-loading">
        <div className="glass-card rounded-2xl p-8 max-w-md w-full text-center">
          <Loader2 size={40} className="animate-spin text-sacred-gold mx-auto mb-4" />
          <p className="text-text-muted font-body text-sm">{t('checkout.checkingSession')}</p>
        </div>
      </div>
    );
  }

  // Returning from PayPal without a session or guest token — the only case
  // where we genuinely can't proceed without signing in
  if (!user && ppToken && !(() => { try { return sessionStorage.getItem(`tw_guest_${ppToken}`); } catch { return null; } })()) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 pt-24 pb-16">
        <ScrollReveal>
          <div className="glass-card rounded-2xl p-8 max-w-md w-full text-center" data-testid="checkout-login-required">
            <ShieldCheck size={48} className="text-sacred-gold/60 mx-auto mb-4" />
            <h2 className="text-xl font-display text-heading mb-2">{t('checkout.loginRequired')}</h2>
            <p className="text-text-muted font-body text-sm mb-6">{t('checkout.loginBody')}</p>
            <button onClick={() => navigate('/auth', { state: { from: location.pathname + location.search } })} className="gold-button px-6 py-2.5 rounded-full font-body" data-testid="checkout-login-btn">{t('checkout.loginBtn')}</button>
          </div>
        </ScrollReveal>
      </div>
    );
  }

  if (!itemId && !ppToken) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 pt-24 pb-16">
        <div className="glass-card rounded-2xl p-8 max-w-md w-full text-center">
          <p className="text-text-muted font-body">{t('checkout.noItem')}</p>
          <button onClick={() => navigate(-1)} className="gold-button px-6 py-2.5 rounded-full font-body mt-4">{t('checkout.goBack')}</button>
        </div>
      </div>
    );
  }

  // Processing
  if (ppToken && processing) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 pt-24 pb-16" data-testid="checkout-processing">
        <div className="glass-card rounded-2xl p-8 max-w-md w-full text-center">
          <Loader2 size={48} className="animate-spin text-sacred-gold mx-auto mb-4" />
          <h2 className="text-xl font-display text-heading mb-2">{t('checkout.processingTitle')}</h2>
          <p className="text-text-muted font-body text-sm">{t('checkout.processingBody')}</p>
        </div>
      </div>
    );
  }

  // Result
  if (paymentResult) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 pt-24 pb-16" data-testid="payment-result">
        <ScrollReveal>
          <div className="glass-card rounded-2xl p-8 max-w-md w-full text-center">
            {paymentResult.success ? (
              <>
                <CheckCircle size={56} className="text-accent-success mx-auto mb-4" />
                <h2 className="text-2xl font-display text-heading mb-2">{t('checkout.successTitle')}</h2>
                <p className="text-text-muted font-body text-sm mb-1">{t('checkout.successThanks')}</p>
                {paymentResult.itemName && <p className="text-warm-brown font-body text-sm mb-1">{paymentResult.itemName}</p>}
                {paymentResult.deliveryType === 'international_lulu' && (
                  <p className="text-sacred-gold font-body text-xs mt-2">{t('checkout.luluNote')}</p>
                )}
                {paymentResult.deliveryType === 'pickup' && (
                  <p className="text-sacred-gold font-body text-xs mt-2">{t('checkout.pickupNote')}</p>
                )}
                {paymentResult.isGuest && (
                  <p className="text-warm-brown font-body text-xs mt-3 px-3 py-2 rounded-lg bg-sacred-gold/5 border border-sacred-gold/10" data-testid="guest-account-note">
                    {t('checkout.guestAccountCreated')}
                  </p>
                )}
                <p className="text-text-muted/50 font-body text-xs mb-6 mt-2">{t('checkout.orderLabel')} {paymentResult.orderId}</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button onClick={() => navigate(`/track-order?order_id=${paymentResult.orderId}`)} className="gold-button px-6 py-2.5 rounded-full font-body flex items-center gap-2 justify-center" data-testid="payment-track-btn">
                    <Package size={14} /> {t('checkout.trackOrder')}
                  </button>
                  <button onClick={() => navigate('/')} className="glass-button px-6 py-2.5 rounded-full font-body text-sm" data-testid="payment-success-home-btn">{t('checkout.returnHome')}</button>
                </div>
              </>
            ) : (
              <>
                <XCircle size={56} className="text-accent-error mx-auto mb-4" />
                <h2 className="text-2xl font-display text-heading mb-2">{t('checkout.failedTitle')}</h2>
                <p className="text-text-muted font-body text-sm mb-6">{paymentResult.message}</p>
                <button onClick={() => navigate('/book')} className="gold-button px-6 py-2.5 rounded-full font-body" data-testid="payment-retry-btn">{t('checkout.backToBook')}</button>
              </>
            )}
          </div>
          {paymentResult.success && paymentResult.bundleSelected && (
            <div className="max-w-md w-full">
              <OrbitReferralCard orderId={paymentResult.orderId} />
            </div>
          )}
        </ScrollReveal>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="checkout-page">
      <div className="max-w-2xl mx-auto">
        <ScrollReveal>
          <button onClick={() => step === 'payment' ? setStep('address') : navigate(-1)} className="flex items-center gap-2 text-text-muted hover:text-heading font-body text-sm mb-6 transition-colors" data-testid="checkout-back-btn">
            <ArrowLeft size={16} /> {step === 'payment' ? t('checkout.backToAddress') : t('checkout.back')}
          </button>
          <h1 className="text-3xl sm:text-4xl font-display text-heading mb-8" data-testid="checkout-title">{t('checkout.title')}</h1>
        </ScrollReveal>

        {/* Order Summary */}
        <ScrollReveal delay={0.05}>
          <div className="glass-card rounded-xl p-6 mb-6" data-testid="order-summary">
            <h3 className="text-text-muted font-body text-xs uppercase tracking-wider mb-4">{t('checkout.orderSummary')}</h3>
            {loading ? (
              <div className="flex items-center justify-center py-4"><Loader2 size={20} className="animate-spin text-sacred-gold" /></div>
            ) : item ? (
              <div>
                <div className="flex items-start gap-4">
                  {item.thumbnail_url && (
                    <img src={item.thumbnail_url.startsWith('http') ? item.thumbnail_url : `${BACKEND_URL}${item.thumbnail_url}`} alt="" className="w-16 h-20 object-cover rounded-lg border border-sacred-gold/10" />
                  )}
                  <div className="flex-1">
                    <h4 className="font-body text-heading font-medium">{localizedField(item.title)}</h4>
                    <p className="text-text-muted text-xs font-body mt-1 capitalize">{isPhysical ? t('checkout.physicalCopy') : (item.type === 'digital' ? t('checkout.digitalCopy') : itemType)}</p>
                  </div>
                  <div className="text-end">
                    <p className="text-xl font-display text-deep-brown">{currencySymbol}{price?.toFixed(2)}</p>
                  </div>
                </div>
                {step === 'payment' && (
                  <div className="mt-4 pt-4 border-t border-sacred-gold/10 space-y-2">
                    <div className="flex justify-between text-sm font-body">
                      <span className="text-text-muted">{t('checkout.subtotal')}</span>
                      <span className="text-heading">{currencySymbol}{price?.toFixed(2)}</span>
                    </div>
                    {isPhysical && deliveryInfo && (
                      <div className="flex justify-between text-sm font-body">
                        <span className="text-text-muted">{t('checkout.shippingLabel')} ({deliveryLabel(usePickup ? 'pickup' : deliveryInfo.delivery_type)})</span>
                        <span className="text-heading">{usePickup ? t('checkout.free') : `${currencySymbol}${deliveryCost.toFixed(2)}`}</span>
                      </div>
                    )}
                    {tipValue > 0 && (
                      <div className="flex justify-between text-sm font-body" data-testid="summary-tip-line">
                        <span className="text-text-muted">{t('checkout.tip')}</span>
                        <span className="text-sacred-gold">+{currencySymbol}{tipValue.toFixed(2)}</span>
                      </div>
                    )}
                    {bundleAmount > 0 && (
                      <div className="flex justify-between text-sm font-body" data-testid="summary-bundle-line">
                        <span className="text-text-muted">{localizedField(bundle?.title) || 'Orbit Starter Pack'}</span>
                        <span className="text-sacred-gold">+{currencySymbol}{bundleAmount.toFixed(2)}</span>
                      </div>
                    )}
                    {recoveryInfo?.valid && recoveryDiscount > 0 && (
                      <div className="flex justify-between text-sm font-body" data-testid="summary-recovery-line">
                        <span className="text-accent-success flex items-center gap-1">{t('checkout.returnGift')}</span>
                        <span className="text-accent-success">−{currencySymbol}{recoveryDiscount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm font-body font-semibold pt-2 border-t border-sacred-gold/10" data-testid="summary-total-line">
                      <span className="text-heading">{t('checkout.total')}</span>
                      <span className="text-deep-brown text-lg">{currencySymbol}{totalPrice.toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>
            ) : <p className="text-text-muted font-body text-sm text-center py-4">{t('checkout.itemNotFound')}</p>}
          </div>
        </ScrollReveal>

        {/* Guest details (no account) */}
        {!user && step === 'address' && (
          <ScrollReveal delay={0.08}>
            <div className="glass-card rounded-xl p-6 mb-6" data-testid="guest-checkout-card">
              <div className="flex items-start justify-between gap-4 mb-1">
                <h3 className="text-heading font-body text-sm font-semibold flex items-center gap-2">
                  <ShieldCheck size={16} className="text-sacred-gold" /> {t('checkout.guestTitle')}
                </h3>
                <button
                  type="button"
                  onClick={() => navigate('/auth', { state: { from: location.pathname + location.search } })}
                  className="text-xs font-body text-sacred-gold hover:text-molten-gold whitespace-nowrap"
                  data-testid="guest-signin-link"
                >
                  {t('checkout.orSignIn')} {t('checkout.signIn')}
                </button>
              </div>
              <p className="text-text-muted/70 font-body text-xs mb-4">{t('checkout.guestSubtitle')}</p>
              <div className="space-y-3">
                <input
                  type="email"
                  value={guestEmail}
                  onChange={e => setGuestEmail(e.target.value)}
                  placeholder={t('checkout.guestEmail')}
                  required
                  autoComplete="email"
                  className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm"
                  data-testid="guest-email"
                />
                <input
                  type="text"
                  value={guestName}
                  onChange={e => { setGuestName(e.target.value); if (!shippingName) setShippingName(e.target.value); }}
                  placeholder={t('checkout.guestName')}
                  autoComplete="name"
                  className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm"
                  data-testid="guest-name"
                />
              </div>
            </div>
          </ScrollReveal>
        )}

        {/* Step: Address (Physical only) */}
        {step === 'address' && isPhysical && (
          <ScrollReveal delay={0.1}>
            <div className="glass-card rounded-xl p-6 mb-6" data-testid="shipping-form">
              <h3 className="text-heading font-body text-sm font-semibold mb-4 flex items-center gap-2">
                <MapPin size={16} className="text-sacred-gold" /> {t('checkout.shippingAddress')}
              </h3>
              <div className="space-y-3">
                <input type="text" value={shippingName} onChange={e => setShippingName(e.target.value)} placeholder={t('checkout.fullName')} className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm" data-testid="ship-name" required />
                <input type="text" value={address1} onChange={e => setAddress1(e.target.value)} placeholder={t('checkout.address1')} className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm" data-testid="ship-address1" required />
                <input type="text" value={address2} onChange={e => setAddress2(e.target.value)} placeholder={t('checkout.address2')} className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm" data-testid="ship-address2" />
                <div className="grid grid-cols-2 gap-3">
                  <input type="text" value={city} onChange={e => setCity(e.target.value)} placeholder={t('checkout.city')} className="px-4 py-3 rounded-xl glass-input font-body text-sm" data-testid="ship-city" required />
                  <input type="text" value={stateCode} onChange={e => setStateCode(e.target.value)} placeholder={t('checkout.stateRegion')} className="px-4 py-3 rounded-xl glass-input font-body text-sm" data-testid="ship-state" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input type="text" value={zip} onChange={e => setZip(e.target.value)} placeholder={t('checkout.zip')} className="px-4 py-3 rounded-xl glass-input font-body text-sm" data-testid="ship-zip" required />
                  <select value={countryCode} onChange={e => setCountryCode(e.target.value)} className="px-4 py-3 rounded-xl glass-input font-body text-sm" data-testid="ship-country">
                    <option value="IL">Israel</option>
                    <option value="US">United States</option>
                    <option value="GB">United Kingdom</option>
                    <option value="DE">Germany</option>
                    <option value="FR">France</option>
                    <option value="CA">Canada</option>
                    <option value="AU">Australia</option>
                    <option value="AE">UAE</option>
                    <option value="SA">Saudi Arabia</option>
                    <option value="JO">Jordan</option>
                    <option value="EG">Egypt</option>
                    <option value="LB">Lebanon</option>
                    <option value="TR">Turkey</option>
                    <option value="NL">Netherlands</option>
                    <option value="IT">Italy</option>
                    <option value="ES">Spain</option>
                    <option value="BR">Brazil</option>
                    <option value="IN">India</option>
                    <option value="JP">Japan</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder={t('checkout.phone')} className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm" data-testid="ship-phone" required />
              </div>

              {/* Delivery info */}
              {calcLoading && (
                <div className="flex items-center gap-2 mt-4 text-text-muted">
                  <Loader2 size={14} className="animate-spin" />
                  <span className="font-body text-xs">{t('checkout.calculating')}</span>
                </div>
              )}
              {calcError && !calcLoading && (
                <div className="mt-4 p-4 rounded-lg bg-accent-error/5 border border-accent-error/15 flex items-center justify-between gap-3" data-testid="delivery-calc-error">
                  <p className="text-accent-error font-body text-xs">{t('checkout.calcFailed')}</p>
                  <button onClick={calculateDelivery} className="glass-button px-4 py-1.5 rounded-full font-body text-xs shrink-0" data-testid="delivery-retry-btn">{t('checkout.retry')}</button>
                </div>
              )}
              {deliveryInfo && !calcLoading && (
                <div className="mt-4 p-4 rounded-lg bg-sacred-gold/5 border border-sacred-gold/10" data-testid="delivery-info">
                  <div className="flex items-center gap-2 mb-2">
                    <Truck size={14} className="text-sacred-gold" />
                    <span className="font-body text-sm font-medium text-heading">{deliveryLabel(deliveryInfo.delivery_type)}</span>
                  </div>
                  <p className="font-body text-sm text-heading">
                    {deliveryInfo.currency === 'ILS' ? '\u20AA' : '$'}{deliveryInfo.delivery_cost.toFixed(2)} {deliveryInfo.currency}
                  </p>
                  {deliveryInfo.pickup_available && (
                    <div className="mt-3 pt-3 border-t border-sacred-gold/10">
                      <label className="flex items-center gap-3 cursor-pointer" data-testid="pickup-option">
                        <input type="checkbox" checked={usePickup} onChange={e => setUsePickup(e.target.checked)} className="accent-[#A67C00]" />
                        <div className="flex items-center gap-2">
                          <Store size={14} className="text-sacred-gold" />
                          <span className="font-body text-sm text-heading">{t('checkout.pickupInstead')}</span>
                        </div>
                      </label>
                      {usePickup && deliveryInfo.pickup_locations?.length > 0 && (
                        <div className="mt-2 ms-7 space-y-1">
                          {deliveryInfo.pickup_locations.map((loc, i) => (
                            <p key={i} className="font-body text-xs text-text-muted">{loc.name} — {loc.address}</p>
                          ))}
                        </div>
                      )}
                      {usePickup && (
                        <button onClick={handleContactForPickup} className="mt-2 ms-7 text-sacred-gold font-body text-xs hover:text-molten-gold underline" data-testid="contact-pickup-btn">
                          {t('checkout.contactPickup')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Save as default address */}
            {isPhysical && !usePickup && (
              <label className="flex items-center gap-2 mb-4 cursor-pointer text-sm font-body text-text-muted" data-testid="save-default-address">
                <input type="checkbox" checked={saveAsDefault} onChange={e => setSaveAsDefault(e.target.checked)} className="accent-[#c8a55c]" />
                {t('checkout.saveDefault')}
              </label>
            )}

            <button onClick={handleProceedToPayment} disabled={!item} className="w-full gold-button px-6 py-3.5 rounded-full font-body text-sm flex items-center justify-center gap-2" data-testid="proceed-to-payment-btn">
              <Package size={16} /> {t('checkout.continuePayment')}
            </button>
          </ScrollReveal>
        )}

        {/* Step: Payment */}
        {(step === 'payment' || !isPhysical) && (
          <ScrollReveal delay={0.1}>
            {/* Orbit Starter Pack upsell (product purchases only) */}
            {bundle && itemType === 'product' && (
              <div
                className={`glass-card rounded-xl p-5 mb-4 border-2 transition ${bundleSelected ? 'border-sacred-gold bg-sacred-gold/5' : 'border-sacred-gold/20'}`}
                data-testid="bundle-offer"
              >
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={bundleSelected}
                    onChange={e => setBundleSelected(e.target.checked)}
                    className="accent-[#c8a55c] w-4 h-4 mt-1"
                    data-testid="bundle-toggle"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="px-2 py-0.5 rounded-full bg-sacred-gold/15 text-sacred-gold text-[10px] font-body uppercase tracking-wider">Limited pre-order</span>
                      {bundle.savings_label && (
                        <span className="px-2 py-0.5 rounded-full bg-accent-success/15 text-accent-success text-[10px] font-body" data-testid="bundle-savings">
                          {localizedField(bundle.savings_label)}
                        </span>
                      )}
                    </div>
                    <h3 className="text-heading font-body text-base font-semibold mt-2" data-testid="bundle-title">
                      {localizedField(bundle.title)}
                    </h3>
                    <p className="text-text-muted font-body text-xs mt-1" data-testid="bundle-subtitle">
                      {localizedField(bundle.subtitle)}
                    </p>
                    {Array.isArray(bundle.perks) && bundle.perks.length > 0 && (
                      <ul className="mt-3 space-y-1">
                        {bundle.perks.map((perk, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-xs font-body text-heading">
                            <CheckCircle size={12} className="text-sacred-gold mt-0.5 flex-shrink-0" />
                            <span>{localizedField(perk)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="flex items-baseline gap-2 mt-3">
                      {bundle.original_price_usd && (
                        <span className="text-text-muted/60 font-body text-xs line-through" data-testid="bundle-original-price">
                          {currencySymbol}{(currency === 'USD' ? bundle.original_price_usd : bundle.original_price_ils)?.toFixed(2)}
                        </span>
                      )}
                      {referralInfo?.valid && bundleBase > 0 ? (
                        <>
                          <span className="text-text-muted/60 font-body text-xs line-through" data-testid="bundle-price-prediscount">
                            {currencySymbol}{bundleBase.toFixed(2)}
                          </span>
                          <span className="text-sacred-gold font-display text-xl" data-testid="bundle-price">
                            {currencySymbol}{bundleAmount.toFixed(2)}
                          </span>
                          <span className="px-2 py-0.5 rounded-full bg-accent-success/15 text-accent-success text-[10px] font-body" data-testid="bundle-referral-badge">
                            −20% friend gift
                          </span>
                        </>
                      ) : (
                        <span className="text-sacred-gold font-display text-xl" data-testid="bundle-price">
                          {currencySymbol}{(currency === 'USD' ? bundle.price_usd : bundle.price_ils)?.toFixed(2)}
                        </span>
                      )}
                    </div>
                    {referralInfo?.valid && (
                      <p className="text-accent-success font-body text-[11px] mt-2" data-testid="bundle-referral-note">
                        Gift from <span className="font-semibold">{referralInfo.referrer_name}</span> — 20% off Orbit Starter Pack applied.
                      </p>
                    )}
                    {bundle.footer_note && (
                      <p className="text-text-muted/50 font-body text-[10px] mt-2 italic">
                        {localizedField(bundle.footer_note)}
                      </p>
                    )}
                  </div>
                </label>
              </div>
            )}

            {/* Tip */}
            <div className="glass-card rounded-xl p-5 mb-4" data-testid="tip-section">
              <div className="flex items-center gap-2 mb-3">
                <Heart size={16} className="text-sacred-gold" />
                <h3 className="text-heading font-body text-sm font-semibold">{t('checkout.tipTitle')}</h3>
              </div>
              <p className="text-text-muted/70 font-body text-xs mb-3">
                {t('checkout.tipBody')}
              </p>
              <div className="flex flex-wrap gap-2">
                {(currency === 'ILS' ? [0, 10, 20, 35] : [0, 3, 5, 10]).map(v => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => { setTipAmount(v); setTipCustom(''); }}
                    className={`px-4 py-2 rounded-full font-body text-sm transition ${(!tipCustom && Number(tipAmount) === v) ? 'gold-button' : 'glass-button text-text-muted'}`}
                    data-testid={`tip-preset-${v}`}
                  >
                    {v === 0 ? t('checkout.noTip') : `${currency === 'ILS' ? '₪' : '$'}${v}`}
                  </button>
                ))}
                <div className="flex items-center gap-1">
                  <span className="text-text-muted font-body text-sm">{currency === 'ILS' ? '₪' : '$'}</span>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    placeholder={t('checkout.custom')}
                    value={tipCustom}
                    onChange={e => {
                      const v = e.target.value;
                      setTipCustom(v);
                      setTipAmount(Number(v) || 0);
                    }}
                    className="w-24 px-3 py-2 rounded-full glass-input font-body text-sm"
                    data-testid="tip-custom-input"
                  />
                </div>
              </div>
            </div>

            {/* Gift */}
            <div className="glass-card rounded-xl p-5 mb-4" data-testid="gift-section">
              <label className="flex items-center gap-3 cursor-pointer" data-testid="gift-toggle">
                <input
                  type="checkbox"
                  checked={isGift}
                  onChange={e => setIsGift(e.target.checked)}
                  className="accent-[#c8a55c] w-4 h-4"
                />
                <Gift size={16} className="text-sacred-gold" />
                <span className="text-heading font-body text-sm font-semibold">{t('checkout.giftTitle')}</span>
              </label>
              {isGift && (
                <div className="mt-4 space-y-3 ps-7">
                  <input
                    type="email"
                    value={giftRecipientEmail}
                    onChange={e => setGiftRecipientEmail(e.target.value)}
                    placeholder={t('checkout.giftEmail')}
                    className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm"
                    data-testid="gift-recipient-email"
                    required
                  />
                  <input
                    type="text"
                    value={giftRecipientName}
                    onChange={e => setGiftRecipientName(e.target.value)}
                    placeholder={t('checkout.giftName')}
                    className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm"
                    data-testid="gift-recipient-name"
                  />
                  <textarea
                    value={giftMessage}
                    onChange={e => setGiftMessage(e.target.value.slice(0, 500))}
                    placeholder={t('checkout.giftMessage')}
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm resize-none"
                    data-testid="gift-message"
                  />
                  <p className="text-text-muted/50 text-xs font-body">
                    {t('checkout.giftNote')}
                  </p>
                </div>
              )}
            </div>

            {/* Total preview */}
            {Number(tipAmount) > 0 && (
              <div className="glass-card rounded-xl px-5 py-3 mb-4 flex items-center justify-between" data-testid="tip-total-preview">
                <span className="text-text-muted font-body text-xs">{t('checkout.tipAdded')}</span>
                <span className="text-sacred-gold font-body text-sm font-semibold">
                  +{currency === 'ILS' ? '₪' : '$'}{Number(tipAmount).toFixed(2)}
                </span>
              </div>
            )}

            <PaymentMethods
              config={paypalConfig}
              currency={currency}
              buildOrderPayload={buildOrderPayload}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
            />
            {error && (
              <div className="mt-4 p-4 rounded-lg bg-accent-error/5 border border-accent-error/15" data-testid="checkout-error">
                <p className="text-accent-error font-body text-sm">{error}</p>
              </div>
            )}
          </ScrollReveal>
        )}
      </div>
    </div>
  );
}
